import { FormEvent, useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import type {
  Agency,
  Calendar,
  CalendarDate,
  FeedInfo,
  GtfsImportReport,
  ListResponse,
  Route,
  ShapeFull,
  ShapeListItem,
  Stop,
  StopTime,
  Trip,
} from "../api/types";

type Tab =
  | "import-export"
  | "agencies"
  | "routes"
  | "stops"
  | "calendars"
  | "shapes"
  | "trips"
  | "feed-info";

const TABS: { id: Tab; label: string }[] = [
  { id: "import-export", label: "Import / Export" },
  { id: "agencies", label: "Agencies" },
  { id: "routes", label: "Routes" },
  { id: "stops", label: "Stops" },
  { id: "calendars", label: "Calendars" },
  { id: "shapes", label: "Shapes" },
  { id: "trips", label: "Trips" },
  { id: "feed-info", label: "Feed Info" },
];

function unwrap<T>(v: ListResponse<T> | T[] | undefined | null): T[] {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (Array.isArray((v as ListResponse<T>).data)) return (v as ListResponse<T>).data;
  return [];
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function GtfsPage() {
  const { session } = useAuth();
  const role = (session?.role ?? "").toLowerCase();
  const canManage = role === "admin" || role === "operations_supervisor";
  const agencyId = session?.agencyId ?? "";

  const [tab, setTab] = useState<Tab>("import-export");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  return (
    <div className="page">
      <h2>GTFS</h2>
      <div className="tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            className={"tab" + (tab === t.id ? " active" : "")}
            onClick={() => {
              setTab(t.id);
              setError(null);
              setSuccess(null);
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      {tab === "import-export" && (
        <ImportExport agencyId={agencyId} canManage={canManage} setError={setError} setSuccess={setSuccess} />
      )}
      {tab === "agencies" && (
        <AgenciesTab canManage={canManage} setError={setError} setSuccess={setSuccess} />
      )}
      {tab === "routes" && (
        <RoutesTab agencyId={agencyId} canManage={canManage} setError={setError} setSuccess={setSuccess} />
      )}
      {tab === "stops" && (
        <StopsTab agencyId={agencyId} canManage={canManage} setError={setError} setSuccess={setSuccess} />
      )}
      {tab === "calendars" && (
        <CalendarsTab agencyId={agencyId} canManage={canManage} setError={setError} setSuccess={setSuccess} />
      )}
      {tab === "shapes" && (
        <ShapesTab agencyId={agencyId} canManage={canManage} setError={setError} setSuccess={setSuccess} />
      )}
      {tab === "trips" && (
        <TripsTab canManage={canManage} setError={setError} setSuccess={setSuccess} />
      )}
      {tab === "feed-info" && (
        <FeedInfoTab agencyId={agencyId} canManage={canManage} setError={setError} setSuccess={setSuccess} />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Import / Export
// ──────────────────────────────────────────────────────────────────────────────

function ImportExport({ agencyId, canManage, setError, setSuccess }: {
  agencyId: string; canManage: boolean;
  setError: (s: string | null) => void; setSuccess: (s: string | null) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<GtfsImportReport | null>(null);

  async function onExport() {
    if (!agencyId) { setError("No agency in session"); return; }
    setBusy(true); setError(null);
    try {
      const blob = await api.exportGTFS(agencyId);
      downloadBlob(blob, `gtfs_${agencyId.slice(0, 8)}.zip`);
      setSuccess("Exported");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Export failed");
    } finally { setBusy(false); }
  }

  async function onImport(e: FormEvent) {
    e.preventDefault();
    if (!file || !agencyId) return;
    setBusy(true); setError(null); setSuccess(null); setReport(null);
    try {
      const r = await api.importGTFS(agencyId, file);
      setReport(r);
      setSuccess(`Imported: ${r.routes} routes, ${r.stops} stops, ${r.trips} trips, ${r.stop_times} stop_times`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed");
    } finally { setBusy(false); }
  }

  return (
    <section>
      <h3>Export</h3>
      <button className="btn primary" onClick={onExport} disabled={busy || !agencyId}>
        Download GTFS zip
      </button>

      <h3 style={{ marginTop: 24 }}>Import</h3>
      {!canManage && <p className="muted">Read-only role. Import disabled.</p>}
      <form onSubmit={onImport}>
        <input
          type="file"
          accept=".zip"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          disabled={!canManage}
        />
        <button className="btn primary" type="submit" disabled={!file || busy || !canManage}>
          Upload + ingest
        </button>
      </form>
      {report && (
        <div className="card" style={{ marginTop: 12 }}>
          <h4>Import report</h4>
          <ul>
            <li>routes: {report.routes}</li>
            <li>stops: {report.stops}</li>
            <li>calendars: {report.calendars} (+{report.calendar_dates} exceptions)</li>
            <li>shapes: {report.shapes}</li>
            <li>trips: {report.trips}</li>
            <li>stop_times: {report.stop_times}</li>
            <li>feed_info: {report.feed_info_loaded ? "loaded" : "not present"}</li>
          </ul>
          {report.warnings && report.warnings.length > 0 && (
            <details>
              <summary>{report.warnings.length} warnings</summary>
              <ul>
                {report.warnings.slice(0, 50).map((w, i) => (<li key={i}>{w}</li>))}
                {report.warnings.length > 50 && <li>…{report.warnings.length - 50} more</li>}
              </ul>
            </details>
          )}
        </div>
      )}
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Agencies
// ──────────────────────────────────────────────────────────────────────────────

function AgenciesTab({ canManage, setError, setSuccess }: {
  canManage: boolean;
  setError: (s: string | null) => void; setSuccess: (s: string | null) => void;
}) {
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [draft, setDraft] = useState<Partial<Agency>>({});

  async function load() {
    try {
      const r = await api.listAgencies();
      setAgencies(unwrap(r));
    } catch (err) { setError(err instanceof Error ? err.message : "Load failed"); }
  }
  useEffect(() => { load(); }, []);

  function edit(a: Agency) { setDraft({ ...a }); }
  function reset() { setDraft({}); }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!draft.agency_id || !draft.name) { setError("agency_id and name required"); return; }
    try {
      if (draft.id) await api.updateAgency(draft.id, draft);
      else await api.createAgency(draft);
      setSuccess("Saved"); reset(); await load();
    } catch (err) { setError(err instanceof Error ? err.message : "Save failed"); }
  }

  async function remove(id: string) {
    if (!confirm("Delete agency? Cascades to all routes/stops/trips.")) return;
    try { await api.deleteAgency(id); setSuccess("Deleted"); await load(); }
    catch (err) { setError(err instanceof Error ? err.message : "Delete failed"); }
  }

  return (
    <section>
      <table className="table">
        <thead><tr><th>GTFS ID</th><th>Name</th><th>Timezone</th><th>URL</th><th></th></tr></thead>
        <tbody>
          {agencies.map((a) => (
            <tr key={a.id}>
              <td>{a.agency_id}</td><td>{a.name}</td><td>{a.timezone}</td><td>{a.url}</td>
              <td>
                {canManage && <button className="btn" onClick={() => edit(a)}>Edit</button>}
                {canManage && <button className="btn danger" onClick={() => remove(a.id)}>Delete</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {canManage && (
        <form onSubmit={save} className="card" style={{ marginTop: 16 }}>
          <h4>{draft.id ? "Edit" : "New"} agency</h4>
          <label>agency_id <input value={draft.agency_id ?? ""} onChange={(e) => setDraft({ ...draft, agency_id: e.target.value })} required /></label>
          <label>name <input value={draft.name ?? ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} required /></label>
          <label>url <input value={draft.url ?? ""} onChange={(e) => setDraft({ ...draft, url: e.target.value })} /></label>
          <label>timezone <input value={draft.timezone ?? ""} onChange={(e) => setDraft({ ...draft, timezone: e.target.value })} placeholder="Asia/Karachi" /></label>
          <label>language <input value={draft.language ?? ""} onChange={(e) => setDraft({ ...draft, language: e.target.value })} placeholder="en" /></label>
          <label>phone <input value={draft.phone ?? ""} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} /></label>
          <div>
            <button className="btn primary" type="submit">Save</button>
            {draft.id && <button className="btn" type="button" onClick={reset}>Cancel</button>}
          </div>
        </form>
      )}
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Routes
// ──────────────────────────────────────────────────────────────────────────────

const ROUTE_TYPES: { v: number; l: string }[] = [
  { v: 0, l: "0 Tram/Light Rail" }, { v: 1, l: "1 Subway/Metro" },
  { v: 2, l: "2 Rail" }, { v: 3, l: "3 Bus" }, { v: 4, l: "4 Ferry" },
  { v: 5, l: "5 Cable Tram" }, { v: 6, l: "6 Aerial Lift" }, { v: 7, l: "7 Funicular" },
  { v: 11, l: "11 Trolleybus" }, { v: 12, l: "12 Monorail" },
];

function RoutesTab({ agencyId, canManage, setError, setSuccess }: {
  agencyId: string; canManage: boolean;
  setError: (s: string | null) => void; setSuccess: (s: string | null) => void;
}) {
  const [routes, setRoutes] = useState<Route[]>([]);
  const [draft, setDraft] = useState<Partial<Route>>({ route_type: 3, route_color: "FFFFFF", route_text_color: "000000" });

  async function load() {
    try { setRoutes(unwrap(await api.listRoutes())); }
    catch (err) { setError(err instanceof Error ? err.message : "Load failed"); }
  }
  useEffect(() => { load(); }, []);

  function edit(r: Route) { setDraft({ ...r }); }
  function reset() { setDraft({ route_type: 3, route_color: "FFFFFF", route_text_color: "000000", agency_id: agencyId }); }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!draft.route_id || !draft.route_long_name) { setError("route_id and route_long_name required"); return; }
    try {
      const body = { ...draft, agency_id: draft.agency_id ?? agencyId };
      if (draft.id) await api.updateRoute(draft.id, body);
      else await api.createRoute(body);
      setSuccess("Saved"); reset(); await load();
    } catch (err) { setError(err instanceof Error ? err.message : "Save failed"); }
  }

  async function remove(id: string) {
    if (!confirm("Delete route? Cascades to trips + templates.")) return;
    try { await api.deleteRoute(id); setSuccess("Deleted"); await load(); }
    catch (err) { setError(err instanceof Error ? err.message : "Delete failed"); }
  }

  return (
    <section>
      <table className="table">
        <thead><tr><th>route_id</th><th>Short</th><th>Long</th><th>Type</th><th></th></tr></thead>
        <tbody>
          {routes.map((r) => (
            <tr key={r.id}>
              <td>{r.route_id}</td><td>{r.route_short_name}</td><td>{r.route_long_name}</td><td>{r.route_type}</td>
              <td>
                {canManage && <button className="btn" onClick={() => edit(r)}>Edit</button>}
                {canManage && <button className="btn danger" onClick={() => remove(r.id)}>Delete</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {canManage && (
        <form onSubmit={save} className="card" style={{ marginTop: 16 }}>
          <h4>{draft.id ? "Edit" : "New"} route</h4>
          <label>route_id <input value={draft.route_id ?? ""} onChange={(e) => setDraft({ ...draft, route_id: e.target.value })} required /></label>
          <label>short name <input value={draft.route_short_name ?? ""} onChange={(e) => setDraft({ ...draft, route_short_name: e.target.value })} /></label>
          <label>long name <input value={draft.route_long_name ?? ""} onChange={(e) => setDraft({ ...draft, route_long_name: e.target.value })} required /></label>
          <label>type <select value={draft.route_type ?? 3} onChange={(e) => setDraft({ ...draft, route_type: Number(e.target.value) })}>
            {ROUTE_TYPES.map((rt) => <option key={rt.v} value={rt.v}>{rt.l}</option>)}
          </select></label>
          <label>color <input value={draft.route_color ?? ""} onChange={(e) => setDraft({ ...draft, route_color: e.target.value })} placeholder="FFFFFF" /></label>
          <label>text color <input value={draft.route_text_color ?? ""} onChange={(e) => setDraft({ ...draft, route_text_color: e.target.value })} placeholder="000000" /></label>
          <div>
            <button className="btn primary" type="submit">Save</button>
            {draft.id && <button className="btn" type="button" onClick={reset}>Cancel</button>}
          </div>
        </form>
      )}
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Stops
// ──────────────────────────────────────────────────────────────────────────────

function StopsTab({ agencyId, canManage, setError, setSuccess }: {
  agencyId: string; canManage: boolean;
  setError: (s: string | null) => void; setSuccess: (s: string | null) => void;
}) {
  const [stops, setStops] = useState<Stop[]>([]);
  const [draft, setDraft] = useState<Partial<Stop> & { stop_lat?: number; stop_lon?: number }>({});
  const [filter, setFilter] = useState("");

  async function load() {
    try { setStops(unwrap(await api.listStops())); }
    catch (err) { setError(err instanceof Error ? err.message : "Load failed"); }
  }
  useEffect(() => { load(); }, []);

  function edit(s: Stop) {
    setDraft({
      ...s,
      stop_lat: s.location?.latitude,
      stop_lon: s.location?.longitude,
    });
  }
  function reset() { setDraft({ agency_id: agencyId, location_type: 0 }); }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!draft.stop_id || !draft.stop_name) { setError("stop_id and stop_name required"); return; }
    if (draft.stop_lat == null || draft.stop_lon == null) { setError("lat/lon required"); return; }
    try {
      const body = { ...draft, agency_id: draft.agency_id ?? agencyId };
      if (draft.id) await api.updateStop(draft.id, body);
      else await api.createStop(body);
      setSuccess("Saved"); reset(); await load();
    } catch (err) { setError(err instanceof Error ? err.message : "Save failed"); }
  }

  async function remove(id: string) {
    if (!confirm("Delete stop?")) return;
    try { await api.deleteStop(id); setSuccess("Deleted"); await load(); }
    catch (err) { setError(err instanceof Error ? err.message : "Delete failed"); }
  }

  const filtered = useMemo(() =>
    stops.filter((s) =>
      !filter ||
      s.stop_id.toLowerCase().includes(filter.toLowerCase()) ||
      s.stop_name.toLowerCase().includes(filter.toLowerCase())
    ), [stops, filter]);

  return (
    <section>
      <input className="search" placeholder="Filter stops…" value={filter} onChange={(e) => setFilter(e.target.value)} />
      <table className="table">
        <thead><tr><th>stop_id</th><th>Name</th><th>Lat</th><th>Lon</th><th></th></tr></thead>
        <tbody>
          {filtered.map((s) => (
            <tr key={s.id}>
              <td>{s.stop_id}</td><td>{s.stop_name}</td>
              <td>{s.location?.latitude?.toFixed(6)}</td>
              <td>{s.location?.longitude?.toFixed(6)}</td>
              <td>
                {canManage && <button className="btn" onClick={() => edit(s)}>Edit</button>}
                {canManage && <button className="btn danger" onClick={() => remove(s.id)}>Delete</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {canManage && (
        <form onSubmit={save} className="card" style={{ marginTop: 16 }}>
          <h4>{draft.id ? "Edit" : "New"} stop</h4>
          <label>stop_id <input value={draft.stop_id ?? ""} onChange={(e) => setDraft({ ...draft, stop_id: e.target.value })} required /></label>
          <label>stop_code <input value={draft.stop_code ?? ""} onChange={(e) => setDraft({ ...draft, stop_code: e.target.value })} /></label>
          <label>stop_name <input value={draft.stop_name ?? ""} onChange={(e) => setDraft({ ...draft, stop_name: e.target.value })} required /></label>
          <label>stop_desc <input value={draft.stop_desc ?? ""} onChange={(e) => setDraft({ ...draft, stop_desc: e.target.value })} /></label>
          <label>lat <input type="number" step="0.000001" value={draft.stop_lat ?? ""} onChange={(e) => setDraft({ ...draft, stop_lat: Number(e.target.value) })} required /></label>
          <label>lon <input type="number" step="0.000001" value={draft.stop_lon ?? ""} onChange={(e) => setDraft({ ...draft, stop_lon: Number(e.target.value) })} required /></label>
          <label>zone_id <input value={draft.zone_id ?? ""} onChange={(e) => setDraft({ ...draft, zone_id: e.target.value })} /></label>
          <label>location_type
            <select value={draft.location_type ?? 0} onChange={(e) => setDraft({ ...draft, location_type: Number(e.target.value) })}>
              <option value={0}>0 stop/platform</option>
              <option value={1}>1 station</option>
              <option value={2}>2 entrance/exit</option>
              <option value={3}>3 generic node</option>
              <option value={4}>4 boarding area</option>
            </select>
          </label>
          <label>wheelchair_boarding
            <select value={draft.wheelchair_boarding ?? 0} onChange={(e) => setDraft({ ...draft, wheelchair_boarding: Number(e.target.value) })}>
              <option value={0}>0 unknown</option>
              <option value={1}>1 accessible</option>
              <option value={2}>2 not accessible</option>
            </select>
          </label>
          <div>
            <button className="btn primary" type="submit">Save</button>
            {draft.id && <button className="btn" type="button" onClick={reset}>Cancel</button>}
          </div>
        </form>
      )}
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Calendars + exceptions
// ──────────────────────────────────────────────────────────────────────────────

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;

function CalendarsTab({ agencyId, canManage, setError, setSuccess }: {
  agencyId: string; canManage: boolean;
  setError: (s: string | null) => void; setSuccess: (s: string | null) => void;
}) {
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [draft, setDraft] = useState<Partial<Calendar>>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [exceptions, setExceptions] = useState<CalendarDate[]>([]);
  const [exDate, setExDate] = useState("");
  const [exType, setExType] = useState<1 | 2>(1);

  async function load() {
    if (!agencyId) return;
    try { setCalendars(await api.listCalendars(agencyId)); }
    catch (err) { setError(err instanceof Error ? err.message : "Load failed"); }
  }
  useEffect(() => { load(); }, [agencyId]);
  useEffect(() => {
    if (!selectedId) { setExceptions([]); return; }
    api.listCalendarDates(selectedId)
      .then(setExceptions)
      .catch((err) => setError(err instanceof Error ? err.message : "Load exceptions failed"));
  }, [selectedId]);

  function edit(c: Calendar) { setDraft({ ...c }); setSelectedId(c.id); }
  function reset() {
    setDraft({
      agency_id: agencyId,
      monday: true, tuesday: true, wednesday: true, thursday: true,
      friday: false, saturday: false, sunday: false,
    });
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!draft.service_id || !draft.start_date || !draft.end_date) {
      setError("service_id, start_date, end_date required"); return;
    }
    try {
      const body = { ...draft, agency_id: draft.agency_id ?? agencyId };
      await api.upsertCalendar(body);
      setSuccess("Saved"); reset(); await load();
    } catch (err) { setError(err instanceof Error ? err.message : "Save failed"); }
  }

  async function remove(id: string) {
    if (!confirm("Delete calendar? Cascades to trips.")) return;
    try { await api.deleteCalendar(id); setSuccess("Deleted"); await load(); }
    catch (err) { setError(err instanceof Error ? err.message : "Delete failed"); }
  }

  async function addException(e: FormEvent) {
    e.preventDefault();
    if (!selectedId || !exDate) return;
    try {
      await api.upsertCalendarDate(selectedId, exDate, exType);
      setExceptions(await api.listCalendarDates(selectedId));
      setExDate("");
    } catch (err) { setError(err instanceof Error ? err.message : "Add exception failed"); }
  }
  async function removeException(date: string) {
    if (!selectedId) return;
    try {
      await api.deleteCalendarDate(selectedId, date.slice(0, 10));
      setExceptions(await api.listCalendarDates(selectedId));
    } catch (err) { setError(err instanceof Error ? err.message : "Remove exception failed"); }
  }

  return (
    <section>
      <table className="table">
        <thead><tr><th>service_id</th><th>Days</th><th>From</th><th>To</th><th></th></tr></thead>
        <tbody>
          {calendars.map((c) => (
            <tr key={c.id}>
              <td>{c.service_id}</td>
              <td>{DAYS.map((d) => (c as any)[d] ? d[0].toUpperCase() : "·").join("")}</td>
              <td>{c.start_date.slice(0, 10)}</td><td>{c.end_date.slice(0, 10)}</td>
              <td>
                <button className="btn" onClick={() => edit(c)}>{selectedId === c.id ? "Selected" : "Edit"}</button>
                {canManage && <button className="btn danger" onClick={() => remove(c.id)}>Delete</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {canManage && (
        <form onSubmit={save} className="card" style={{ marginTop: 16 }}>
          <h4>{draft.id ? "Edit" : "New"} calendar</h4>
          <label>service_id <input value={draft.service_id ?? ""} onChange={(e) => setDraft({ ...draft, service_id: e.target.value })} required /></label>
          <div>
            {DAYS.map((d) => (
              <label key={d} style={{ marginRight: 8 }}>
                <input type="checkbox" checked={Boolean((draft as any)[d])}
                  onChange={(e) => setDraft({ ...draft, [d]: e.target.checked })} /> {d}
              </label>
            ))}
          </div>
          <label>start_date <input type="date" value={(draft.start_date ?? "").slice(0, 10)} onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} required /></label>
          <label>end_date <input type="date" value={(draft.end_date ?? "").slice(0, 10)} onChange={(e) => setDraft({ ...draft, end_date: e.target.value })} required /></label>
          <div>
            <button className="btn primary" type="submit">Save</button>
            {draft.id && <button className="btn" type="button" onClick={reset}>Cancel</button>}
          </div>
        </form>
      )}
      {selectedId && (
        <div className="card" style={{ marginTop: 16 }}>
          <h4>Exceptions for {calendars.find((c) => c.id === selectedId)?.service_id}</h4>
          <ul>
            {exceptions.map((x) => (
              <li key={x.id}>
                {x.exception_date.slice(0, 10)} — type {x.exception_type} ({x.exception_type === 1 ? "added" : "removed"})
                {canManage && <button className="btn danger" onClick={() => removeException(x.exception_date)}>×</button>}
              </li>
            ))}
          </ul>
          {canManage && (
            <form onSubmit={addException}>
              <input type="date" value={exDate} onChange={(e) => setExDate(e.target.value)} required />
              <select value={exType} onChange={(e) => setExType(Number(e.target.value) as 1 | 2)}>
                <option value={1}>1 added</option>
                <option value={2}>2 removed</option>
              </select>
              <button className="btn primary" type="submit">Add</button>
            </form>
          )}
        </div>
      )}
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Shapes (textual point editor)
// ──────────────────────────────────────────────────────────────────────────────

function ShapesTab({ agencyId, canManage, setError, setSuccess }: {
  agencyId: string; canManage: boolean;
  setError: (s: string | null) => void; setSuccess: (s: string | null) => void;
}) {
  const [shapes, setShapes] = useState<ShapeListItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [full, setFull] = useState<ShapeFull | null>(null);
  const [shapeGtfsId, setShapeGtfsId] = useState("");
  const [pointsText, setPointsText] = useState("");

  async function load() {
    if (!agencyId) return;
    try { setShapes(await api.listShapes(agencyId)); }
    catch (err) { setError(err instanceof Error ? err.message : "Load failed"); }
  }
  useEffect(() => { load(); }, [agencyId]);
  useEffect(() => {
    if (!selectedId) { setFull(null); return; }
    api.getShape(selectedId)
      .then((s) => {
        setFull(s);
        setShapeGtfsId(s.shape_id);
        setPointsText(s.geometry.coordinates.map(([lon, lat]) => `${lat},${lon}`).join("\n"));
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Load shape failed"));
  }, [selectedId]);

  function newShape() {
    setSelectedId(null); setFull(null); setShapeGtfsId(""); setPointsText("");
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    const pts = pointsText.split("\n")
      .map((l) => l.trim()).filter(Boolean)
      .map((l) => {
        const [a, b] = l.split(",").map((s) => Number(s.trim()));
        return { lat: a, lon: b };
      });
    if (pts.length < 2 || pts.some((p) => Number.isNaN(p.lat) || Number.isNaN(p.lon))) {
      setError("Provide ≥ 2 valid 'lat,lon' lines"); return;
    }
    try {
      await api.upsertShape({ agency_id: agencyId, shape_id: shapeGtfsId, points: pts });
      setSuccess("Saved"); await load();
    } catch (err) { setError(err instanceof Error ? err.message : "Save failed"); }
  }

  async function remove(id: string) {
    if (!confirm("Delete shape?")) return;
    try { await api.deleteShape(id); setSuccess("Deleted"); newShape(); await load(); }
    catch (err) { setError(err instanceof Error ? err.message : "Delete failed"); }
  }

  return (
    <section>
      <table className="table">
        <thead><tr><th>shape_id</th><th>Points</th><th></th></tr></thead>
        <tbody>
          {shapes.map((s) => (
            <tr key={s.id}>
              <td>{s.shape_id}</td><td>{s.points}</td>
              <td>
                <button className="btn" onClick={() => setSelectedId(s.id)}>Edit</button>
                {canManage && <button className="btn danger" onClick={() => remove(s.id)}>Delete</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {canManage && (
        <form onSubmit={save} className="card" style={{ marginTop: 16 }}>
          <h4>{full ? "Edit" : "New"} shape</h4>
          <button className="btn" type="button" onClick={newShape}>+ New</button>
          <label>shape_id <input value={shapeGtfsId} onChange={(e) => setShapeGtfsId(e.target.value)} required /></label>
          <label>points (one "lat,lon" per line, ≥ 2)
            <textarea rows={10} value={pointsText} onChange={(e) => setPointsText(e.target.value)} required />
          </label>
          <div><button className="btn primary" type="submit">Save</button></div>
        </form>
      )}
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Trips + stop_times editor
// ──────────────────────────────────────────────────────────────────────────────

function TripsTab({ canManage, setError, setSuccess }: {
  canManage: boolean;
  setError: (s: string | null) => void; setSuccess: (s: string | null) => void;
}) {
  const [routes, setRoutes] = useState<Route[]>([]);
  const [routeId, setRouteId] = useState("");
  const [trips, setTrips] = useState<Trip[]>([]);
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [stopTimes, setStopTimes] = useState<StopTime[]>([]);
  const [stops, setStops] = useState<Stop[]>([]);

  useEffect(() => {
    api.listRoutes().then((r) => setRoutes(unwrap(r))).catch(() => {});
    api.listStops().then((s) => setStops(unwrap(s))).catch(() => {});
  }, []);

  async function loadTrips() {
    if (!routeId) { setTrips([]); return; }
    try {
      const r = await api.listTrips({ route_id: routeId, limit: 200 });
      setTrips(unwrap(r));
    } catch (err) { setError(err instanceof Error ? err.message : "Load trips failed"); }
  }
  useEffect(() => { loadTrips(); }, [routeId]);

  async function selectTrip(t: Trip) {
    setSelectedTrip(t);
    try { setStopTimes(await api.getTripStopTimes(t.id)); }
    catch (err) { setError(err instanceof Error ? err.message : "Load stop_times failed"); }
  }

  function updateST(i: number, patch: Partial<StopTime>) {
    setStopTimes((arr) => arr.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  }
  function addST() {
    setStopTimes((arr) => [...arr, {
      stop_id: stops[0]?.id ?? "", stop_sequence: arr.length + 1,
      arrival_time: "00:00:00", departure_time: "00:00:00",
      pickup_type: 0, drop_off_type: 0, timepoint: true,
    }]);
  }
  function removeST(i: number) {
    setStopTimes((arr) => arr.filter((_, idx) => idx !== i));
  }
  async function saveST() {
    if (!selectedTrip) return;
    try {
      await api.replaceTripStopTimes(selectedTrip.id, stopTimes);
      setSuccess(`Saved ${stopTimes.length} stop_times`);
    } catch (err) { setError(err instanceof Error ? err.message : "Save failed"); }
  }

  return (
    <section>
      <label>Route
        <select value={routeId} onChange={(e) => setRouteId(e.target.value)}>
          <option value="">(pick a route)</option>
          {routes.map((r) => <option key={r.id} value={r.id}>{r.route_id} {r.route_short_name && `(${r.route_short_name})`}</option>)}
        </select>
      </label>
      <table className="table">
        <thead><tr><th>trip_id</th><th>Headsign</th><th>Direction</th><th></th></tr></thead>
        <tbody>
          {trips.map((t) => (
            <tr key={t.id}>
              <td>{t.trip_id}</td><td>{t.trip_headsign}</td><td>{t.direction_id}</td>
              <td><button className="btn" onClick={() => selectTrip(t)}>{selectedTrip?.id === t.id ? "Selected" : "Edit stop_times"}</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      {selectedTrip && (
        <div className="card" style={{ marginTop: 16 }}>
          <h4>stop_times for {selectedTrip.trip_id}</h4>
          <table className="table">
            <thead><tr><th>Seq</th><th>Stop</th><th>Arrival</th><th>Departure</th><th>Pickup</th><th>Drop-off</th><th>TP</th><th></th></tr></thead>
            <tbody>
              {stopTimes.map((st, i) => (
                <tr key={i}>
                  <td><input type="number" value={st.stop_sequence} onChange={(e) => updateST(i, { stop_sequence: Number(e.target.value) })} disabled={!canManage} /></td>
                  <td>
                    <select value={st.stop_id} onChange={(e) => updateST(i, { stop_id: e.target.value })} disabled={!canManage}>
                      {stops.map((s) => <option key={s.id} value={s.id}>{s.stop_id} {s.stop_name}</option>)}
                    </select>
                  </td>
                  <td><input value={st.arrival_time} onChange={(e) => updateST(i, { arrival_time: e.target.value })} disabled={!canManage} placeholder="HH:MM:SS" /></td>
                  <td><input value={st.departure_time} onChange={(e) => updateST(i, { departure_time: e.target.value })} disabled={!canManage} placeholder="HH:MM:SS" /></td>
                  <td><input type="number" value={st.pickup_type ?? 0} onChange={(e) => updateST(i, { pickup_type: Number(e.target.value) })} disabled={!canManage} /></td>
                  <td><input type="number" value={st.drop_off_type ?? 0} onChange={(e) => updateST(i, { drop_off_type: Number(e.target.value) })} disabled={!canManage} /></td>
                  <td><input type="checkbox" checked={Boolean(st.timepoint)} onChange={(e) => updateST(i, { timepoint: e.target.checked })} disabled={!canManage} /></td>
                  <td>{canManage && <button className="btn danger" onClick={() => removeST(i)}>×</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {canManage && (
            <div>
              <button className="btn" onClick={addST}>+ Add row</button>
              <button className="btn primary" onClick={saveST}>Save all</button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Feed info
// ──────────────────────────────────────────────────────────────────────────────

function FeedInfoTab({ agencyId, canManage, setError, setSuccess }: {
  agencyId: string; canManage: boolean;
  setError: (s: string | null) => void; setSuccess: (s: string | null) => void;
}) {
  const [info, setInfo] = useState<FeedInfo>({
    agency_id: agencyId,
    feed_publisher_name: "",
    feed_publisher_url: "",
    feed_lang: "en",
  });

  useEffect(() => {
    if (!agencyId) return;
    api.getFeedInfo(agencyId)
      .then((f) => setInfo(f))
      .catch((err) => {
        // 404 ok — no feed_info yet
        const msg = err instanceof Error ? err.message : String(err);
        if (!msg.toLowerCase().includes("not found")) setError(msg);
      });
  }, [agencyId]);

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!info.feed_publisher_name || !info.feed_publisher_url) {
      setError("publisher name and url required"); return;
    }
    try {
      const { id, agency_id, ...body } = info;
      await api.upsertFeedInfo(agencyId, body);
      setSuccess("Saved");
    } catch (err) { setError(err instanceof Error ? err.message : "Save failed"); }
  }

  return (
    <form onSubmit={save} className="card">
      <h4>Feed info for agency {agencyId.slice(0, 8)}</h4>
      <label>feed_publisher_name <input value={info.feed_publisher_name} onChange={(e) => setInfo({ ...info, feed_publisher_name: e.target.value })} required disabled={!canManage} /></label>
      <label>feed_publisher_url <input value={info.feed_publisher_url} onChange={(e) => setInfo({ ...info, feed_publisher_url: e.target.value })} required disabled={!canManage} /></label>
      <label>feed_lang <input value={info.feed_lang} onChange={(e) => setInfo({ ...info, feed_lang: e.target.value })} disabled={!canManage} /></label>
      <label>default_lang <input value={info.default_lang ?? ""} onChange={(e) => setInfo({ ...info, default_lang: e.target.value })} disabled={!canManage} /></label>
      <label>feed_version <input value={info.feed_version ?? ""} onChange={(e) => setInfo({ ...info, feed_version: e.target.value })} disabled={!canManage} /></label>
      <label>feed_start_date <input type="date" value={(info.feed_start_date ?? "").slice(0, 10)} onChange={(e) => setInfo({ ...info, feed_start_date: e.target.value })} disabled={!canManage} /></label>
      <label>feed_end_date <input type="date" value={(info.feed_end_date ?? "").slice(0, 10)} onChange={(e) => setInfo({ ...info, feed_end_date: e.target.value })} disabled={!canManage} /></label>
      <label>feed_contact_email <input value={info.feed_contact_email ?? ""} onChange={(e) => setInfo({ ...info, feed_contact_email: e.target.value })} disabled={!canManage} /></label>
      <label>feed_contact_url <input value={info.feed_contact_url ?? ""} onChange={(e) => setInfo({ ...info, feed_contact_url: e.target.value })} disabled={!canManage} /></label>
      {canManage && <button className="btn primary" type="submit">Save</button>}
    </form>
  );
}
